// Purpose: grouped multi-select with chip-count trigger button. Used by
// admin/Audit.tsx to filter by audit verb across categorized groups.
// Click-outside to close; Escape closes; checkboxes within groups; Clear
// All resets the selection. CSP-clean (no inline styles, no
// dangerouslySetInnerHTML).
//
// Commit 8b.12.1: popover renders through createPortal into document.body
// to escape parent stacking contexts. The previous z-50 absolute layout
// was being painted under sibling form fields whose ancestors created
// stacking contexts the relative wrapper couldn't supersede. Portal +
// position-by-rect is the same pattern Modal uses (it's `fixed inset-0`
// at the React root); both bypass the form-element stacking trap.

import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import { ChevronDownIcon } from '../icons/index.tsx';
import { usePopoverPosition } from '../../lib/popover-position.ts';

export interface MultiSelectOption {
  value: string;
  label: string;
}

export interface MultiSelectGroup {
  label: string;
  options: MultiSelectOption[];
}

export interface MultiSelectProps {
  groups: MultiSelectGroup[];
  selected: string[];
  onChange: (next: string[]) => void;
  /** Rendered when selected is empty (e.g. "All actions"). */
  placeholder?: ReactNode;
  /** "Action filter", "Status", etc. — leading label inside the trigger. */
  prefix?: ReactNode;
  className?: string;
}

export function MultiSelect({
  groups,
  selected,
  onChange,
  placeholder = 'All',
  prefix,
  className,
}: MultiSelectProps) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const position = usePopoverPosition(triggerRef, open);

  // Click-outside + Escape close the popover. Mousedown (not click) so a
  // click on a checkbox inside the popover doesn't first dismiss the
  // popover via the document-click listener. The popover lives in
  // document.body via portal, so check both refs — wrapperRef alone
  // wouldn't contain the popover post-portal.
  useEffect(() => {
    if (!open) return;
    const onMouse = (ev: MouseEvent): void => {
      const target = ev.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (ev: KeyboardEvent): void => {
      if (ev.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onMouse);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouse);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedSet = new Set(selected);
  const toggle = (value: string): void => {
    const next = new Set(selectedSet);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange([...next]);
  };

  // Set the popover position via CSS custom properties on the popover
  // element itself (no `style={{}}` prop — keeps the SPA's CSP
  // style-src 'self' clean per Commit 8b.8). Mirrors the ProgressFill
  // pattern (apps/web/src/components/ui/LoadingStates.tsx + the
  // .popover-portal class in styles.css).
  useEffect(() => {
    const el = popoverRef.current;
    if (!el || !position) return;
    el.style.setProperty('--popover-top', `${position.top}px`);
    el.style.setProperty('--popover-left', `${position.left}px`);
  }, [position]);

  return (
    <div className={['inline-block', className ?? ''].join(' ')}>
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={[
          'inline-flex items-center gap-2 h-10 px-3',
          'bg-bg-secondary border border-border-default rounded-md',
          'text-body-m text-fg-primary',
          'hover:border-border-strong',
          'focus:border-accent-primary focus:outline-none',
          'transition-colors duration-fast ease-out',
        ].join(' ')}
      >
        {prefix ? <span className="text-fg-muted">{prefix}</span> : null}
        <span>
          {selected.length === 0 ? (
            <span className="text-fg-muted">{placeholder}</span>
          ) : (
            <span className="font-medium">
              {selected.length} {selected.length === 1 ? 'verb' : 'verbs'}
            </span>
          )}
        </span>
        <ChevronDownIcon size={16} className="text-fg-muted" aria-hidden />
      </button>

      {open && position && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={popoverRef}
              role="listbox"
              className={[
                'popover-portal mt-2 z-50',
                'min-w-[320px] max-w-[420px] max-h-[480px] overflow-y-auto',
                'bg-bg-elevated border border-border-default rounded-md',
                'shadow-lg p-2',
              ].join(' ')}
            >
              <div className="flex items-center justify-between px-2 py-1">
                <span className="text-label uppercase text-fg-secondary">
                  {selected.length} selected
                </span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  disabled={selected.length === 0}
                  className={[
                    'text-body-s underline-offset-2',
                    selected.length === 0
                      ? 'text-fg-muted cursor-not-allowed'
                      : 'text-accent-primary hover:underline',
                  ].join(' ')}
                >
                  Clear
                </button>
              </div>

              {groups.map((group) => (
                <div key={group.label} className="mt-2">
                  <div className="px-2 py-1 text-label uppercase text-fg-muted border-t border-border-subtle">
                    {group.label}
                  </div>
                  <div className="flex flex-col">
                    {group.options.map((opt) => {
                      const checked = selectedSet.has(opt.value);
                      return (
                        <label
                          key={opt.value}
                          className={[
                            'flex items-center gap-2 px-2 py-1.5 rounded-sm cursor-pointer',
                            'hover:bg-bg-tertiary',
                          ].join(' ')}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggle(opt.value)}
                            className="w-4 h-4"
                          />
                          <span className="text-body-s text-fg-primary">{opt.label}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
